// pages/ReportsPage.jsx
import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import DatePicker from "react-bootstrap-daterangepicker";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-daterangepicker/daterangepicker.css";

import { useNotification } from "../context/NotificationContext";
import { PageHeader, Card } from "../components/UI";
import * as api from "../services/dynamicPriceApi";

const companyId = localStorage.getItem("companyid") || 1;

// Source colors — keyed consistently
const SRC_CLR = {
  PMS: { color: "#6d28d9", bg: "#f5f3ff", border: "#c4b5fd" },
  BE: { color: "#0369a1", bg: "#e0f2fe", border: "#7dd3fc" },
  OTA: { color: "#be185d", bg: "#fdf2f8", border: "#f9a8d4" },
};

const ROOM_CLR = ["#2563eb", "#d97706", "#8b5cf6", "#16a34a", "#dc2626", "#8b5cf6"];

function fmt(v) {
  const n = Number(v || 0);
  return `₹${n.toLocaleString("en-IN")}`;
}

function num(v, d = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
}

function SourceBar({ source, generated, approved, uplift_pct }) {
  const cc = SRC_CLR[source] || {
    color: "#64748b",
    bg: "#f8fafc",
    border: "#e2e8f0",
  };
  const pct = Math.max(0, num(uplift_pct));
  const bar = Math.min((pct / 40) * 100, 100);

  return (
    <div style={{ marginBottom: 20 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 8,
          gap: 10,
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span
            style={{
              fontSize: 10,
              fontWeight: 800,
              color: cc.color,
              background: cc.bg,
              border: `1.5px solid ${cc.border}`,
              borderRadius: 5,
              padding: "3px 10px",
              letterSpacing: 0.5,
            }}
          >
            {source}
          </span>
          <span style={{ fontSize: 13, color: "#475569", fontWeight: 500 }}>
            {num(generated)} recs &nbsp;·&nbsp; {num(approved)} approved
          </span>
        </div>
        <span
          style={{
            fontFamily: "'DM Mono', monospace",
            fontSize: 14,
            fontWeight: 800,
            color: "#15803d",
          }}
        >
          +{pct}%
        </span>
      </div>
      <div style={{ background: "#f1f5f9", borderRadius: 5, height: 10, overflow: "hidden" }}>
        <div
          style={{
            width: `${bar}%`,
            height: "100%",
            background: cc.color,
            borderRadius: 5,
            transition: "width 0.6s ease",
          }}
        />
      </div>
    </div>
  );
}

function RoomBar({ label, value, max, color, pctLabel }) {
  const v = num(value);
  const w = max ? (v / max) * 100 : 0;

  return (
    <div style={{ marginBottom: 18 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 6,
          gap: 10,
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{label}</span>
        <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, color: "#64748b", fontWeight: 500 }}>{pctLabel}</span>
          <span
            style={{
              fontFamily: "'DM Mono', monospace",
              fontSize: 14,
              fontWeight: 800,
              color,
            }}
          >
            {fmt(v)}
          </span>
        </div>
      </div>
      <div style={{ background: "#f1f5f9", borderRadius: 5, height: 10, overflow: "hidden" }}>
        <div
          style={{
            width: `${Math.max(0, Math.min(100, w))}%`,
            height: "100%",
            background: color,
            borderRadius: 5,
            transition: "width 0.6s ease",
          }}
        />
      </div>
    </div>
  );
}

function safeMonthLabel(startDate, endDate) {
  if (!startDate || !endDate) return "Selected Period";
  const s = moment(startDate);
  const e = moment(endDate);
  if (s.isSame(e, "month")) return s.format("MMMM YYYY");
  return `${s.format("DD MMM YYYY")} - ${e.format("DD MMM YYYY")}`;
}

/**
 * Normalize backend payload into UI shape.
 * Accepts several aliases so page remains resilient while backend stabilizes.
 */
function normalizeReportPayload(raw, startDate, endDate) {
  const r = raw?.data ?? raw ?? {};

  const bySourceRaw = Array.isArray(r.by_source)
    ? r.by_source
    : Array.isArray(r.bySource)
    ? r.bySource
    : [];

  const byRoomRaw = Array.isArray(r.by_room)
    ? r.by_room
    : Array.isArray(r.byRoom)
    ? r.byRoom
    : [];

  const topDatesRaw = Array.isArray(r.top_dates)
    ? r.top_dates
    : Array.isArray(r.topDates)
    ? r.topDates
    : [];

  const totalGenerated = num(r.total_generated ?? r.totalGenerated);
  const approved = num(r.approved);
  const modified = num(r.modified);
  const rejected = num(r.rejected);

  const baseRevenue = num(r.base_revenue ?? r.baseRevenue);
  const aiRevenue = num(r.ai_revenue ?? r.aiRevenue);
  const upliftAmount =
    r.uplift_amount != null ? num(r.uplift_amount) : Math.max(0, aiRevenue - baseRevenue);

  const upliftPct =
    r.uplift_pct != null
      ? num(r.uplift_pct)
      : baseRevenue > 0
      ? Number((((aiRevenue - baseRevenue) / baseRevenue) * 100).toFixed(1))
      : 0;

  return {
    month: r.month || safeMonthLabel(startDate, endDate),
    total_generated: totalGenerated,
    approved,
    modified,
    rejected,
    base_revenue: baseRevenue,
    ai_revenue: aiRevenue,
    uplift_amount: upliftAmount,
    uplift_pct: upliftPct,
    by_source: bySourceRaw.map((s) => ({
      source: s.source || s.channel || "UNKNOWN",
      generated: num(s.generated ?? s.total_generated ?? s.total),
      approved: num(s.approved ?? s.approved_count),
      uplift_pct: num(s.uplift_pct ?? s.avg_uplift_pct ?? s.revenue_uplift_pct),
    })),
    by_room: byRoomRaw.map((x) => ({
      type: x.type || x.room_type || x.type_name || "Room Type",
      total_uplift: num(x.total_uplift ?? x.uplift_amount),
      avg_uplift_pct: num(x.avg_uplift_pct ?? x.uplift_pct),
    })),
    top_dates: topDatesRaw.map((d) => ({
      date: d.date || d.report_date || d.checkin_date || "",
      event: d.event || d.event_name || d.reason || "High demand",
      uplift: num(d.uplift ?? d.uplift_amount),
    })),
  };
}

export default function ReportsPage() {
  const { notify } = useNotification();

  const [dateRange, setDateRange] = useState(() => ({
    startDate: moment().startOf("month"),
    endDate: moment().endOf("month"),
  }));

  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [report, setReport] = useState(null);

  const monthParam = useMemo(() => dateRange.startDate.format("YYYY-MM"), [dateRange]);

  const fetchReport = async ({ silent = false } = {}) => {
    if (!companyId) return;

    if (!silent) setLoading(true);

    try {
      // Current API available in your file: getMonthlyReport(companyid, month)
      // We pass month derived from picker start date. If later backend accepts range,
      // you can extend API and switch here without changing UI.
      const res = await api.getMonthlyReport(companyId, monthParam);
      const normalized = normalizeReportPayload(
        res?.data,
        dateRange.startDate,
        dateRange.endDate
      );
      setReport(normalized);
    } catch (err) {
      setReport(null);
      notify(
        "error",
        err?.response?.data?.message || "Failed to load report data."
      );
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, monthParam]);

  const handleDateChange = ({ start, end }) => {
    setDateRange({
      startDate: moment(start).startOf("day"),
      endDate: moment(end).endOf("day"),
    });
  };

  const handleExport = async () => {
    // Your API file currently does not expose a dedicated export endpoint.
    // Keeping graceful UX and using getMonthlyReport for now.
    setExporting(true);
    try {
      await api.getMonthlyReport(companyId, monthParam);
      notify(
        "info",
        "Report data refreshed. PDF export API not added yet in dynamicPriceApi.js."
      );
    } catch (err) {
      notify("error", err?.response?.data?.message || "Export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const approvalRate = useMemo(() => {
    const total = num(report?.total_generated);
    if (!total) return 0;
    return Math.round(
      ((num(report?.approved) + num(report?.modified)) / total) * 100
    );
  }, [report]);

  const barMax = useMemo(() => {
    const arr = Array.isArray(report?.by_room) ? report.by_room : [];
    return arr.length ? Math.max(...arr.map((r) => num(r.total_uplift))) : 0;
  }, [report]);

  const kpis = useMemo(() => {
    const r = report || {};
    return [
      {
        label: "Total Generated",
        value: num(r.total_generated),
        sub: "AI recommendations",
        color: "#2563eb",
      },
      {
        label: "Approved",
        value: num(r.approved),
        sub: "AI price used",
        color: "#16a34a",
      },
      {
        label: "Modified",
        value: num(r.modified),
        sub: "Manager adjusted",
        color: "#d97706",
      },
      {
        label: "Rejected",
        value: num(r.rejected),
        sub: "Base price retained",
        color: "#dc2626",
      },
      {
        label: "Approval Rate",
        value: `${approvalRate}%`,
        sub: "Approved + modified",
        color: "#8b5cf6",
      },
    ];
  }, [report, approvalRate]);

  return (
    <div style={{ padding: "28px 32px" }}>
      <PageHeader
        title="Revenue Impact Reports"
        subtitle="Measurable AI pricing performance — every rupee, explained."
      >
        {/* Date Range Picker (same pattern as your reference) */}
        <DatePicker
          initialSettings={{
            singleDatePicker: false,
            showDropdowns: true,
            autoUpdateInput: false,
            startDate: dateRange.startDate.toDate(),
            endDate: dateRange.endDate.toDate(),
            ranges: {
              Today: [moment().toDate(), moment().toDate()],
              Tomorrow: [
                moment().add(1, "day").toDate(),
                moment().add(1, "day").toDate(),
              ],
              "This Week": [
                moment().startOf("week").toDate(),
                moment().endOf("week").toDate(),
              ],
              "This Month": [
                moment().startOf("month").toDate(),
                moment().endOf("month").toDate(),
              ],
              "Last Month": [
                moment().subtract(1, "month").startOf("month").toDate(),
                moment().subtract(1, "month").endOf("month").toDate(),
              ],
              "Next Month": [
                moment().add(1, "month").startOf("month").toDate(),
                moment().add(1, "month").endOf("month").toDate(),
              ],
            },
            locale: {
              format: "DD MMM YYYY",
              applyLabel: "Select",
              cancelLabel: "Cancel",
            },
          }}
          onCallback={(start, end) => {
            handleDateChange({ start, end });
          }}
        >
          <div className="input-group" style={{ width: "auto" }}>
            <input
              type="text"
              className="form-control"
              value={`${dateRange.startDate.format("DD MMM")} - ${dateRange.endDate.format(
                "DD MMM YYYY"
              )}`}
              style={{
                width: "220px",
                height: "40px",
                fontSize: "13px",
                fontWeight: 700,
                textAlign: "center",
                padding: "0 12px",
                borderRight: "none",
                backgroundColor: "#ffffff",
                borderRadius: "999px 0 0 999px",
              }}
              readOnly
            />
            <div className="input-group-append">
              <span
                className="input-group-text"
                style={{
                  height: "40px",
                  backgroundColor: "#fff",
                  borderLeft: "none",
                  borderRadius: "0 999px 999px 0",
                  cursor: "pointer",
                }}
              >
                <i className="fa fa-chevron-down" style={{ color: "#495057" }} />
              </span>
            </div>
          </div>
        </DatePicker>

        <button
          className="dp-btn dp-btn-outline"
          onClick={handleExport}
          disabled={exporting || loading}
        >
          {exporting ? (
            <>
              <span className="dp-spin">⟳</span> Exporting...
            </>
          ) : (
            "↓ Export PDF"
          )}
        </button>
      </PageHeader>

      {/* Loading State */}
      {loading && (
        <Card style={{ padding: "28px 26px", marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, color: "#475569" }}>
            <span className="dp-spin">⟳</span>
            <span style={{ fontWeight: 700 }}>Loading report...</span>
          </div>
        </Card>
      )}

      {/* Empty/Error */}
      {!loading && !report && (
        <Card style={{ padding: "36px 28px", textAlign: "center" }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>📉</div>
          <div style={{ fontSize: 16, fontWeight: 800, color: "#0f172a", marginBottom: 6 }}>
            Report data unavailable
          </div>
          <div style={{ fontSize: 13, color: "#64748b", marginBottom: 14 }}>
            Please check API response for the selected period and try again.
          </div>
          <button className="dp-btn dp-btn-primary" onClick={() => fetchReport()}>
            Retry
          </button>
        </Card>
      )}

      {!loading && report && (
        <>
          {/* Hero gradient card */}
          <div
            style={{
              background: "linear-gradient(135deg, #0f2a4a 0%, #1d4ed8 60%, #2563eb 100%)",
              borderRadius: 18,
              padding: "36px 40px",
              marginBottom: 26,
              color: "#fff",
              position: "relative",
              overflow: "hidden",
              boxShadow: "0 8px 40px rgba(37,99,235,0.30)",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: -60,
                right: -60,
                width: 260,
                height: 260,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.05)",
              }}
            />
            <div
              style={{
                position: "absolute",
                bottom: -80,
                right: 100,
                width: 180,
                height: 180,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.04)",
              }}
            />
            <div
              style={{
                position: "absolute",
                top: -30,
                right: 200,
                width: 120,
                height: 120,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.03)",
              }}
            />

            <div style={{ position: "relative", zIndex: 1 }}>
              <div
                style={{
                  fontSize: 11,
                  color: "rgba(255,255,255,0.6)",
                  marginBottom: 8,
                  textTransform: "uppercase",
                  letterSpacing: 1.5,
                  fontWeight: 700,
                }}
              >
                Total AI Revenue Uplift — {report.month}
              </div>
              <div
                style={{
                  fontFamily: "'DM Mono', monospace",
                  fontSize: 52,
                  fontWeight: 800,
                  letterSpacing: -2,
                  marginBottom: 8,
                  lineHeight: 1,
                }}
              >
                +{fmt(report.uplift_amount)}
              </div>
              <div
                style={{
                  fontSize: 16,
                  color: "rgba(255,255,255,0.8)",
                  fontWeight: 500,
                  marginBottom: 32,
                }}
              >
                +{num(report.uplift_pct)}% above base revenue &nbsp;·&nbsp; {approvalRate}% approval rate
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 48,
                  paddingTop: 24,
                  borderTop: "1px solid rgba(255,255,255,0.15)",
                  flexWrap: "wrap",
                }}
              >
                {[
                  {
                    label: "Without AI",
                    value: fmt(report.base_revenue),
                    sub: "Base rate revenue",
                  },
                  {
                    label: "With AI",
                    value: fmt(report.ai_revenue),
                    sub: "Approved AI prices",
                  },
                  {
                    label: "Uplift",
                    value: `+${num(report.uplift_pct)}%`,
                    sub: "Revenue improvement",
                  },
                ].map((item) => (
                  <div key={item.label}>
                    <div
                      style={{
                        fontSize: 10,
                        color: "rgba(255,255,255,0.5)",
                        textTransform: "uppercase",
                        letterSpacing: 1,
                        marginBottom: 5,
                        fontWeight: 700,
                      }}
                    >
                      {item.label}
                    </div>
                    <div
                      style={{
                        fontFamily: "'DM Mono', monospace",
                        fontSize: 22,
                        fontWeight: 800,
                      }}
                    >
                      {item.value}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: "rgba(255,255,255,0.4)",
                        marginTop: 3,
                      }}
                    >
                      {item.sub}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* KPI row */}
          <div style={{ display: "flex", gap: 14, marginBottom: 22, flexWrap: "wrap" }}>
            {kpis.map((s) => (
              <div
                key={s.label}
                style={{
                  background: "#fff",
                  borderRadius: 14,
                  padding: "18px 22px",
                  borderLeft: `4px solid ${s.color}`,
                  flex: 1,
                  minWidth: 140,
                  boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    color: "#64748b",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: 0.8,
                    marginBottom: 8,
                  }}
                >
                  {s.label}
                </div>
                <div
                  style={{
                    fontFamily: "'DM Mono', monospace",
                    fontSize: 26,
                    fontWeight: 800,
                    color: "#0f172a",
                    letterSpacing: -0.5,
                    marginBottom: 4,
                  }}
                >
                  {s.value}
                </div>
                <div style={{ fontSize: 12, color: s.color, fontWeight: 600 }}>{s.sub}</div>
              </div>
            ))}
          </div>

          {/* Charts row */}
          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <Card style={{ padding: "24px 26px" }}>
                <div
                  style={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: "#0f172a",
                    marginBottom: 3,
                  }}
                >
                  Performance by Channel
                </div>
                <div style={{ fontSize: 12, color: "#64748b", marginBottom: 22 }}>
                  AI revenue uplift % per booking source
                </div>

                {Array.isArray(report.by_source) && report.by_source.length > 0 ? (
                  report.by_source.map((s) => <SourceBar key={s.source} {...s} />)
                ) : (
                  <div style={{ fontSize: 13, color: "#94a3b8" }}>No channel data available.</div>
                )}
              </Card>
            </div>

            <div className="col-md-6">
              <Card style={{ padding: "24px 26px" }}>
                <div
                  style={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: "#0f172a",
                    marginBottom: 3,
                  }}
                >
                  Performance by Room Type
                </div>
                <div style={{ fontSize: 12, color: "#64748b", marginBottom: 22 }}>
                  Revenue uplift contribution per category
                </div>

                {Array.isArray(report.by_room) && report.by_room.length > 0 ? (
                  report.by_room.map((room, i) => (
                    <RoomBar
                      key={`${room.type}-${i}`}
                      label={room.type}
                      value={room.total_uplift}
                      max={barMax}
                      color={ROOM_CLR[i % ROOM_CLR.length]}
                      pctLabel={`Avg +${num(room.avg_uplift_pct)}%`}
                    />
                  ))
                ) : (
                  <div style={{ fontSize: 13, color: "#94a3b8" }}>No room-type data available.</div>
                )}
              </Card>
            </div>
          </div>

          {/* Top performing dates */}
          <Card style={{ marginBottom: 18, padding: "24px 26px" }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", marginBottom: 3 }}>
              Top Performing Dates
            </div>
            <div style={{ fontSize: 12, color: "#64748b", marginBottom: 20 }}>
              Dates where AI pricing delivered the highest incremental revenue
            </div>

            {Array.isArray(report.top_dates) && report.top_dates.length > 0 ? (
              <div className="row g-3">
                {report.top_dates.slice(0, 3).map((d, i) => {
                  const medals = ["🥇", "🥈", "🥉"];
                  const accents = ["#d97706", "#64748b", "#b45309"];
                  return (
                    <div key={`${d.date}-${i}`} className="col-md-4">
                      <div
                        style={{
                          background: i === 0 ? "#fffbeb" : "#f8fafc",
                          borderRadius: 12,
                          padding: "20px 22px",
                          border: `1px solid ${i === 0 ? "#fde68a" : "#e2e8f0"}`,
                          borderTop: `3px solid ${accents[i]}`,
                        }}
                      >
                        <div style={{ fontSize: 22, marginBottom: 10 }}>
                          {medals[i] || "🏅"}
                        </div>
                        <div
                          style={{
                            fontFamily: "'DM Mono', monospace",
                            fontSize: 12,
                            color: "#64748b",
                            marginBottom: 5,
                            fontWeight: 500,
                          }}
                        >
                          {d.date}
                        </div>
                        <div
                          style={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: "#0f172a",
                            marginBottom: 14,
                          }}
                        >
                          {d.event || "High demand period"}
                        </div>
                        <div
                          style={{
                            fontFamily: "'DM Mono', monospace",
                            fontSize: 26,
                            fontWeight: 800,
                            color: "#15803d",
                          }}
                        >
                          +{fmt(d.uplift)}
                        </div>
                        <div
                          style={{
                            fontSize: 11,
                            color: "#64748b",
                            marginTop: 4,
                            fontWeight: 500,
                          }}
                        >
                          incremental revenue
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ fontSize: 13, color: "#94a3b8" }}>No top-date data available.</div>
            )}
          </Card>

          {/* Summary box */}
          <div
            style={{
              background: "linear-gradient(135deg,#f0fdf4,#dcfce7)",
              border: "1px solid #86efac",
              borderRadius: 14,
              padding: "22px 26px",
            }}
          >
            <div
              style={{
                fontSize: 15,
                fontWeight: 700,
                color: "#166534",
                marginBottom: 16,
              }}
            >
              📊 Monthly Summary — {report.month}
            </div>
            <div className="row g-3">
              {[
                {
                  label: "AI Generated",
                  value: `${num(report.total_generated)} recommendations`,
                },
                {
                  label: "Manager Actioned",
                  value: `${
                    num(report.approved) + num(report.modified) + num(report.rejected)
                  } actioned`,
                },
                {
                  label: "Revenue Uplift",
                  value: `+${fmt(report.uplift_amount)}`,
                },
                {
                  label: "ROI on AI Module",
                  value: "~12× monthly subscription",
                },
              ].map((item) => (
                <div key={item.label} className="col-md-3">
                  <div
                    style={{
                      fontSize: 10,
                      color: "#15803d",
                      textTransform: "uppercase",
                      letterSpacing: 0.8,
                      marginBottom: 5,
                      fontWeight: 800,
                    }}
                  >
                    {item.label}
                  </div>
                  <div
                    style={{
                      fontFamily: "'DM Mono', monospace",
                      fontSize: 15,
                      fontWeight: 800,
                      color: "#14532d",
                    }}
                  >
                    {item.value}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}